import { Box, Button, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { catchError, handleApiError, notify } from '~/utils/helper';
import useDebounce from '~/hooks/useDebounce';
import styles from './Price.module.scss';
import classNames from 'classnames/bind';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import { IPayloadDeletePriceComponent, IPrice } from '~/utils/interface/estimate';
import usePermission from '~/hooks/usePermission';
import EditPriceDialog from '~/components/prices/EditPrice/EditPriceDialog';
import { handleGetTags } from '~/thunks/tag/tagThunk';
import PriceList from '~/components/prices/PriceList/PriceList';
import { handleGetPrice, handleGetPrices } from '~/thunks/price/pricesThunk';
import { priceActions } from '~/thunks/price/pricesSlice';
import priceApi from '~/apis/estimate';
const cx = classNames.bind(styles);

const Price = () => {
  const permissions = usePermission();
  const [searchInput, setSearchInput] = useState<string>('');
  const { t } = useTranslation(['admin/price', 'admin/component']);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const dispatch = useAppDispatch();
  const searchInputDebounce = useDebounce<string>(searchInput);
  const [editData, setEditData] = useState<IPrice>();
  const { filter, needRefreshData, selectedIds, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.price
  );

  const handleSetOpen = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditDialog(open);
  };

  const handleOpenEdit = async (id: string) => {
    const response = await dispatch(handleGetPrice(id)).unwrap();
    setEditData(response);
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);
    try {
    } catch (e) {
      handleApiError(e);
    }
  };
  const handleOpen = async () => {
    await fetchTagData();
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
  };
  const fetchTagData = async () => {
    try {
      await dispatch(handleGetTags()).unwrap();
    } catch (e) {
      handleApiError(e);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(priceActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(priceActions.changeMenuSelectId(undefined));
    dispatch(priceActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedPrice = useCallback(async () => {
    let payloadDeletePriceComponents: IPayloadDeletePriceComponent =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await priceApi.deletePrice(payloadDeletePriceComponents);
      notify('success', t('deleteSuccessMessage'));
      dispatch(priceActions.changeNeedRefreshData(true));
      dispatch(priceActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  useEffect(() => {
    if (dispatch && typeof searchInputDebounce !== 'undefined') {
      dispatch(
        priceActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['name'],
          page: 1,
        })
      );
    }
  }, [searchInputDebounce, dispatch]);

  useEffect(() => {
    dispatch(handleGetPrices(filter))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);

  return (
    <Box className='w-full h-full'>
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <BaseHeaderTitle title={t('title')} />
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            {permissions.price_delete && (
              <Button
                className={cx(
                  { 'visibility: visible': selectedIds.length > 0 },
                  { 'visibility: hidden': selectedIds.length <= 0 }
                )}
                onClick={openAlertDeleteModal}
                variant='contained'
                color='error'
                disableElevation
                startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
              >
                {t('delete')}
              </Button>
            )}
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
          </Box>
        </Box>

        {permissions.price_create && (
          <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleOpen}
            >
              {t('addNew')}
            </Button>
            <EditPriceDialog open={openEditDialog} setOpen={handleSetOpen} mode={editDialogMode} data={editData} />
          </Box>
        )}
      </Box>
      <Box className='mt-[40px]'>
        <PriceList onOpenEdit={handleOpenEdit} data={editData} />
      </Box>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedPrice}
      />
    </Box>
  );
};

export default Price;
